import { useState } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, describeError } from "../lib/api";
import { authClient, looksLikeWrongDoor } from "../lib/auth";
import type { ProfileCard, ProfilePatch, ProfileVisibility } from "@pagemates/shared";
import { BackBar, Confirm, Spinner } from "../components/ui";
import { Group, Saved } from "./club/ClubSettingsPage";
import { cameFrom } from "../lib/returnTo";

import { WaysToSignIn, WrongDoorNote } from "../components/SignIn";
import { useTheme, type Theme } from "../lib/theme";
import { NotificationSettings } from "../components/NotificationSettings";
import { EmailAddresses } from "../components/Emails";
import { useMe } from "../lib/queries";
import { account } from "@pagemates/shared/copy";

const txt = account.settings;

/** Dark, light, or whatever the device says. Per browser; nothing is stored on the server. */
function Appearance() {
  const [theme, setTheme] = useTheme();
  return (
    <section className="card">
      <div className="switch stacked">
        <div><div className="k">{txt.appearance}</div><div className="d">{txt.thisBrowserOnlySystem}</div></div>
        <div className="seg" role="group" aria-label={txt.appearance}>
          {(["system", "dark", "light"] as Theme[]).map((t) => (
            <button key={t} type="button" aria-pressed={theme === t} onClick={() => setTheme(t)}>{{ system: txt.system, dark: txt.dark, light: txt.light }[t]}</button>
          ))}
        </div>
      </div>
    </section>
  );
}
function HardcoverCard({ me }: { me: import("@pagemates/shared").Me }) {
  const qc = useQueryClient();
  const { hardcover: outcome } = useSearch({ from: "/authed/me/settings" });
  const hc = me.hardcover;
  const sync = useMutation({ mutationFn: () => api.hardcoverSync(true), onSuccess: () => qc.invalidateQueries({ queryKey: ["me"] }) });
  const unlink = useMutation({ mutationFn: api.hardcoverUnlink, onSuccess: () => qc.invalidateQueries({ queryKey: ["me"] }) });
  if (!hc.available) return null;

  return (
    <div className="card stack">
      <div className="row spread"><h3>{txt.hardcover}</h3>{hc.linked && <span className={`pill${hc.status === "ok" ? " accent" : ""}`}>{hc.status === "ok" ? txt.connected : txt.needsReconnect}</span>}</div>
      {outcome === "linked" && <p className="ok">{txt.connectedYourProgressWill}</p>}
      {outcome === "denied" && <p className="error">{txt.youCancelledOnHardcover}</p>}
      {outcome === "error" && <p className="error">{txt.thatDidnTGo}</p>}
      {!hc.linked ? (
        <>
          <p className="muted small">{txt.connectYourHardcoverAccount}</p>
          <a className="btn primary" href="/api/hardcover/connect">{txt.connectHardcover}</a>
        </>
      ) : (
        <>
          <p className="muted small">{txt.linkedAs}<strong>{hc.username}</strong>.{" "}
            {hc.status === "broken" ? txt.hardcoverStoppedAcceptingOur : hc.lastSyncAt ? txt.lastSynced(new Date(hc.lastSyncAt).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })) : txt.notSyncedYet}
          </p>
          <div className="row" style={{ flexWrap: "wrap" }}>
            {hc.status === "broken" ? (
              <a className="btn primary sm" href="/api/hardcover/connect">{txt.reconnect}</a>
            ) : (
              <button className="btn sm" disabled={sync.isPending} onClick={() => sync.mutate()}>{sync.isPending ? txt.syncing : txt.syncNow}</button>
            )}
            <Confirm label={txt.disconnect} className="ghost sm" busy={unlink.isPending} onConfirm={() => unlink.mutate()}
              question={txt.disconnectHardcoverProgressAlready} />
          </div>
          {sync.data && <p className="muted small">{sync.data.synced ? txt.syncedUpdate(sync.data.updated, sync.data.updated === 1 ? "" : txt.s) : txt.nothingToSyncRight}</p>}
          {(sync.isError || unlink.isError) && <p className="error">{describeError(sync.error ?? unlink.error)}</p>}
        </>
      )}
    </div>
  );
}

/** Who can see your card, and what of it (PLAN.md §Profiles as library cards). */
function CardSettings({ card }: { card: ProfileCard }) {
  const qc = useQueryClient();
  const own = card.own!;
  const [asking, setAsking] = useState(false);
  const [showToo, setShowToo] = useState(own.showOtherClubBooks);
  const [copied, setCopied] = useState(false);
  const [saved, setSaved] = useState<string | null>(null);
  const set = useMutation({
    mutationFn: (p: ProfilePatch) => api.updateProfile(p),
    onSuccess: async (_d, p) => {
      await qc.invalidateQueries({ queryKey: ["profile"] });
      setAsking(false);
      setSaved(p.visibility ? "visibility" : "other"); window.setTimeout(() => setSaved(null), 1800);
    },
  });
  const choose = (v: ProfileVisibility) => {
    if (v === own.visibility) return;
    if (v === "link") { setShowToo(own.showOtherClubBooks); setAsking(true); return; }
    setAsking(false); set.mutate({ visibility: v });
  };
  const link = own.slug ? `${window.location.origin}/p/${own.slug}` : null;
  const opts: { key: ProfileVisibility; label: string; line: string }[] = [
    { key: "clubs", label: txt.sharedClubs, line: txt.peopleInAClub },
    { key: "private", label: txt.onlyMe, line: txt.clubmatesSeeYourName },
    { key: "link", label: txt.anyoneWithTheLink, line: txt.clubmatesAndAnyoneYou },
  ];
  const { all, inClubs } = own.stampCounts;
  return (
    <>
      <Group title={txt.whoCanSeeYour} about={txt.yourNameAndPhoto}>
        <div className="field"><label>{txt.visibility}<Saved on={saved === "visibility"} /></label>
          <div className="vis-list" role="radiogroup" aria-label={txt.whoCanSeeYour}>
            {opts.map((o) => (
              <button key={o.key} type="button" role="radio" aria-checked={own.visibility === o.key} className={`vis-opt${own.visibility === o.key ? " on" : ""}`} onClick={() => choose(o.key)}>
                <span className="radio-dot" aria-hidden="true" /><span><b>{o.label}</b><span className="d">{o.line}</span></span>
              </button>
            ))}
          </div>
        </div>
        {asking && (
          <div className="vis-confirm card" role="alertdialog" aria-label={txt.confirm}>
            <b>{txt.letAnyoneWithThe}</b>
            <p className="muted small">{txt.anyoneYouSendIt}</p>
            <ul className="allow">
              <li>{txt.yourNamePhotoLine}</li>
              <li>{txt.whenYourCardWas}</li>
              <li>{txt.yourPinnedQuoteSealed}</li>
            </ul>
            {!own.showOtherClubBooks && (
              <>
                <p className="small">{txt.yourStampsComeFrom}<b>{txt.yourCardWillLook}</b>{txt.unlessYouShowBooks}</p>
                <div className="switch">
                  <div><div className="k">{txt.showMyStamps}</div><div className="d">{txt.withoutClubNames}</div></div>
                  <input className="toggle" type="checkbox" checked={showToo} aria-label={txt.showMyStampsTo} onChange={(e) => setShowToo(e.target.checked)} />
                </div>
              </>
            )}
            <p className="muted small">{txt.neverShownWhichClubs}</p>
            {set.isError && <p className="error small">{describeError(set.error)}</p>}
            <div className="row" style={{ gap: 8, justifyContent: "flex-end" }}>
              <button type="button" className="btn ghost sm" onClick={() => setAsking(false)}>{txt.cancel}</button>
              <button type="button" className="btn primary sm" disabled={set.isPending}
                onClick={() => set.mutate({ visibility: "link", ...(showToo !== own.showOtherClubBooks ? { showOtherClubBooks: showToo } : {}) })}>{txt.turnOn}</button>
            </div>
          </div>
        )}
        {own.visibility === "link" && link && (
          <div className="switch">
            <div style={{ minWidth: 0 }}><div className="k">{txt.yourCardSLink}</div><div className="d" style={{ overflowWrap: "anywhere" }}>{link.replace(/^https?:\/\//, "")}</div></div>
            <button type="button" className="btn sm" onClick={() => { void navigator.clipboard?.writeText(link).then(() => { setCopied(true); window.setTimeout(() => setCopied(false), 1800); }); }}>{copied ? txt.copied : txt.copyLink}</button>
          </div>
        )}
      </Group>
      <Group title={txt.booksFromOtherClubs} about={txt.stampsFromClubsThe}>
        <div className="field"><label>{txt.stamps}<Saved on={saved === "other"} /></label>
          <div className="seg" role="group" aria-label={txt.booksFromOtherClubs}>
            <button type="button" aria-pressed={!own.showOtherClubBooks} onClick={() => own.showOtherClubBooks && set.mutate({ showOtherClubBooks: false })}>{txt.hidden}</button>
            <button type="button" aria-pressed={own.showOtherClubBooks} onClick={() => !own.showOtherClubBooks && set.mutate({ showOtherClubBooks: true })}>{txt.shownWithoutClubNames}</button>
          </div>
        </div>
        {all > inClubs && <p className="muted small" style={{ margin: 0 }}>{own.showOtherClubBooks
          ? <>{txt.clubmatesSeeUpTo}<b>{all}</b>{txt.stampsNotJustThe}<b>{inClubs}</b>{txt.fromClubsYouRe}</>
          : <>{txt.clubmatesWillSeeUp}<b>{all}</b>{txt.stampsInsteadOf}<b>{inClubs}</b>.</>}</p>}
      </Group>
    </>
  );
}

export function SettingsPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const me = useMe();
  const card = useQuery({ queryKey: ["profile", "me"], queryFn: () => api.profile("me") });
  const signOut = useMutation({
    mutationFn: () => authClient.signOut(),
    onSuccess: () => { qc.clear(); void navigate({ to: "/", replace: true }); },
  });
  const del = useMutation({
    mutationFn: api.deleteMe,
    onSuccess: () => { qc.clear(); void navigate({ to: "/", replace: true }); },
  });
  const { linked: linkedParam } = useSearch({ from: "/authed/me/settings" });

  if (me.isPending) return <main className="page"><Spinner /></main>;
  if (me.isError || !me.data) return <main className="page"><p className="error">{describeError(me.error)}</p></main>;
  const u = me.data;
  const wrongDoor = looksLikeWrongDoor(u);

  return (
    <main className="page stack settings me-settings">
      <BackBar label={txt.yourCard} link={{ to: "/me" }} onClick={(e) => { if (cameFrom("/me")) { e.preventDefault(); window.history.back(); } }} />
      <h1>{txt.settings}</h1>
      <div className="sgroups">
        {card.data?.own && <CardSettings card={card.data} />}
        <NotificationSettings />
        {/* Everything that was on the profile page before the card, as it was. */}
        <section className="sgroup">
          <header><h2>{txt.account}</h2><p>{txt.howYouSignIn}</p></header>
          <div className="stack">
            {wrongDoor && <WrongDoorNote me={u} />}
            <WaysToSignIn me={u} justLinked={linkedParam} />
            <EmailAddresses />
            <HardcoverCard me={u} />
            <Appearance />
            <div className="stack-sm">
              <button className="btn" onClick={() => signOut.mutate()} disabled={signOut.isPending}>{txt.signOut}</button>
              <Confirm label={txt.deleteMyAccount} busy={del.isPending} onConfirm={() => del.mutate()}
                question={txt.thisRemovesYourProgress} />
              {del.isError && <p className="error">{describeError(del.error)}</p>}
            </div>
          </div>
        </section>
      </div>
      <p className="muted small center"><a href="/policy.html">{txt.policyAndContact}</a></p>
    </main>
  );
}
